import React, { memo } from 'react';
import './styles.scss';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Image, Skeleton, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Icons from '../../../configs/icon.config';
import {
  commonState,
  setTaxInvestmentView,
} from '../../../app/base/commonSlice';

interface CardProps {
  classname?: string;
  figStyles?: React.CSSProperties;
  extfigStyles?: React.CSSProperties;
  hasLink?: boolean;
  hasSubtitle?: boolean;
  hasNote?: boolean;
  title?: string | React.ReactNode;
  subtitle?: string | any;
  note?: string | React.ReactNode;
  label?: string | React.ReactNode;
  location?: string;
  date?: string;
  temp?: string;
  widgetDescription?: string;
  styles?: React.CSSProperties;
  type?: 'figure' | 'widget' | 'section';
  imageUrl?: string;
  darkimageUrl?: string;
  extImageUrl?: string;
  isBlank?: boolean;
  alt_image?: string;
  component?: React.ReactNode;
  iconName?: React.ReactNode;
  exticonName?: string;
  isLoading?: boolean;
  isLocationLoading?: boolean;
  isError?: boolean;
  link?: string;
  cash?: string | React.ReactNode;
  isCash?: boolean;
}

export function Cards({
  classname,
  styles,
  figStyles,
  type,
  hasLink,
  darkimageUrl,
  hasSubtitle,
  hasNote,
  label,
  title,
  isCash,
  subtitle,
  note,
  location,
  date,
  temp,
  widgetDescription,
  imageUrl,
  isBlank,
  component,
  alt_image,
  cash,
  iconName,
  isLoading,
  isLocationLoading,
  exticonName,
  link,
}: CardProps) {
  const cardStyle = {
    // height: 237,
    // width: 880,
    ...styles,
  };
  const classes = classNames('card-wrapper h-full', classname);
  const widClasses = classNames('fig-card wid-card-wrapper', classname);
  const secClasses = classNames('sec-card-wrapper h-full', classname);
  const darkLightClasses = 'text-[#007161] dark:text-[#00BDA3]';
  const { taxInvestmentView } = useSelector(commonState);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleClick = () => {
    dispatch(setTaxInvestmentView(!taxInvestmentView));
  };

  const handleBlur = () => {
    dispatch(setTaxInvestmentView(false));
  };
  const handleMouseLeave = () => {
    dispatch(setTaxInvestmentView(false));
  };

  switch (type) {
    case 'figure':
      return (
        <div className={classes} style={cardStyle}>
          <div
            className="fig-card-img h-full flex items-center justify-center"
            style={figStyles}
          >
            {isBlank ? (
              component
            ) : (
              <img
                className="h-full object-cover"
                // preview={false}
                src={imageUrl}
                alt={alt_image}
              />
            )}
          </div>
        </div>
      );
    case 'widget':
      return (
        <div
          className={classNames(widClasses, 'mb-[20px] sm:mb-[20px] md:mb-0')}
          style={cardStyle}
        >
          <div className="widget-logo flex-shrink-0">
            {iconName ? (
              <div className={classNames('card3-icon', darkLightClasses)}>
                {iconName}
              </div>
            ) : (
              <>
                {cash ? (
                  <>
                    {' '}
                    <img
                      className="w-[80px] dark:hidden block "
                      src={imageUrl}
                      alt="bridge-image"
                    />
                    <img
                      className="w-[80px] dark:block hidden "
                      src={darkimageUrl}
                      alt="bridge-image"
                    />
                  </>
                ) : (
                  <Image
                    style={{ width: 80 }}
                    src={imageUrl}
                    alt="bridge-image"
                  />
                )}
              </>
            )}
          </div>
          <div className="card-content">
            <div className="card-label">{label}</div>
            <div className="card-title">
              {isLoading && !cash ? (
                <Skeleton active paragraph={{ rows: 0 }} />
              ) : (
                title
              )}
              {cash ? (
                <span className="font-semibold text-black dark:text-gray-200 mr-2">
                  {t('tax.note')}
                </span>
              ) : null}
              <Tooltip
                trigger={['click', 'hover']}
                title={!taxInvestmentView ? `${t('taxCash.tooltip')}` : null}
              >
                <span
                  className={`card cursor-pointer ${
                    taxInvestmentView ? 'active' : ''
                  }`}
                  onClick={handleClick}
                  onBlur={handleBlur}
                  onMouseLeave={handleMouseLeave}
                >
                  {isCash ? cash : null}
                </span>
              </Tooltip>
              {hasSubtitle ? (
                <span className="card-subtittle">({subtitle})</span>
              ) : null}
            </div>
            {hasLink ? (
              <a
                href={link}
                target="_blank"
                className="card-link"
                rel="noreferrer"
              >
                {t('event.link')} {Icons.chevronRight}
              </a>
            ) : null}
            {hasNote ? (
              <div className="card-note font-semibold ">{note}</div>
            ) : null}
          </div>
        </div>
      );
    case 'section':
      return (
        <div className={secClasses} style={cardStyle}>
          <div className="fig-card">
            <span className="section-1">
              <div className="welcome-content">
                <span className={classNames('welcome-icon', darkLightClasses)}>
                  {iconName}
                </span>
                <div className="card-content">
                  <div className="card-label">{label}</div>
                  {isLoading ? (
                    <Skeleton active paragraph={{ rows: 0 }} />
                  ) : (
                    <div className="card-title">{title}</div>
                  )}
                </div>
              </div>
            </span>

            <span className="section-2">
              <hr className="divider-desk" />
              <hr className="divider-mobile" />
            </span>

            <span className="section-3">
              <div className="flex flex-row justify-between items-center gap-8">
                <div className="weather-content">
                  <div className="weather-location-date">
                    {isLocationLoading ? (
                      <Skeleton active paragraph={{ rows: 0 }} />
                    ) : (
                      <div className="card-label">{location}</div>
                    )}

                    <div className="card-note-2">{date}</div>
                  </div>
                  <div className="temp-data">
                    {isLoading ? (
                      <Skeleton active paragraph={{ rows: 1 }} />
                    ) : (
                      <>
                        <div className="card-title">{temp}</div>
                        <div className="card-note">{widgetDescription}</div>
                      </>
                    )}
                  </div>
                </div>
                <div className={classNames('card-logo', darkLightClasses)}>
                  <span>
                    {isLoading ? (
                      <Skeleton.Image active />
                    ) : (
                      <img src={exticonName} alt="" />
                    )}
                  </span>
                </div>
              </div>
            </span>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default memo(Cards);
